import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  LeadWorkflowQuery,
  WorkflowStageSearchRequest,
} from "lib/generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

// Cannot generate this from the graphql backend because the transition group enum
// must be of string type to use unified logic and also accept custom stages later
// on when we add that functionality.
export enum LeadWorkflowTransitionGroup {
  InProgress = "In Progress",
  Converted = "Converted",
  Disqualified = "Disqualified",
  Won = "Won",
  Lost = "Lost",
}

const LEAD_WORKFLOW_QUERY = graphql(`
  query leadWorkflow(
    $withCounts: Boolean!
    $stageFilterRequest: WorkflowStageSearchRequest
    $withStageValueSum: Boolean!
  ) {
    leadWorkflow {
      id
      isDefault
      transitions {
        id
        group
        order
        stage {
          id
          name
          color
          iconColor
          iconName
          leadCount(WorkflowStageSearchRequest: $stageFilterRequest) @include(if: $withCounts)
          leadValueSum(WorkflowStageSearchRequest: $stageFilterRequest)
            @include(if: $withStageValueSum)
        }
      }
    }
  }
`);

type UseLeadWorkflowOptions = {
  withCounts?: boolean;
  withStageValueSum?: boolean;
  stageFilterRequest?: WorkflowStageSearchRequest;
};

export type LeadWorkflow = LeadWorkflowQuery["leadWorkflow"];

export function useLeadWorkflow(
  {
    withCounts = false,
    withStageValueSum = false,
    stageFilterRequest,
  }: UseLeadWorkflowOptions = {},
  options?: UseQueryOptions<LeadWorkflowQuery, unknown, LeadWorkflowQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(
    ["leadWorkflow", withCounts, stageFilterRequest, withStageValueSum],
    LEAD_WORKFLOW_QUERY,
    [
      {
        withCounts,
        withStageValueSum,
        stageFilterRequest: stageFilterRequest ?? {},
      },
    ],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
    },
  );
  return { ...rest, data: data?.leadWorkflow };
}
